import { h } from 'preact';
import { useState, useRef, useEffect, useMemo, useCallback } from 'preact/hooks';
import styles from './ProductCardCarousel.module.scss';
/**
 * Per-card image carousel (AQ-1087: mobile-only — desktop keeps the hover
 * image-swap in ProductCard.tsx). Renders up to a handful of product media as
 * a row of full-width slides and pages between them by dragging (touch or
 * mouse) plus tapping a dot; no arrow buttons (removed per AQ-1087 client
 * feedback — they read as visual clutter once swipe is the primary
 * interaction, like paging through an Instagram post).
 *
 * Paging is driven entirely by this component — a `transform: translateX()`
 * on the track, moved by hand from the raw pointer delta — rather than the
 * browser's native scroll-snap. An earlier version used a horizontally
 * scrollable `overflow-x: auto` track and only stepped in for the
 * over-drag-past-an-end wrap case, trusting native touch-scrolling to handle
 * ordinary paging. That never reliably engaged: this carousel lives inside
 * the card's full-tile `<a>`, which needs the *tap* gesture for navigation —
 * exactly the situation where a touchscreen's native scroll-vs-click gesture
 * arbitration is least reliable — so swipes were silently swallowed and nothing
 * paged (AQ-1087 client feedback). Owning the transform ourselves removes that
 * ambiguity: nothing but this code ever moves the track.
 *
 * A released drag always completes the swipe in whichever direction it net
 * moved — however far it went — rather than snapping back unless it crossed
 * some distance threshold; a real Instagram-style swipe reads as binary
 * (this slide or the next one), not proportional.
 *
 * Looping is infinite in both directions: a clone of the last item is
 * rendered before the first slide and a clone of the first item after the
 * last, so paging past either end animates straight into the (visually
 * identical) clone — continuing in the same direction — instead of jumping
 * backwards to slide 0. Once that transition finishes, the track is silently
 * snapped (no animation) from the clone back onto the real slide at the same
 * visual position, so the loop never runs out of clones to advance into.
 *
 * Lives inside the card's `<a>`, so every interactive control calls
 * preventDefault + stopPropagation to avoid triggering navigation / select-item
 * tracking on the anchor.
 *
 * A video slide plays (muted, looped) only while it is the active slide — and
 * only while hovered on a hover-capable device — to keep playback cost down.
 * `preload` stays `none` so videos never download until they actually play.
 */
export function ProductCardCarousel({ items, imagePriority = false }) {
    var _a;
    const trackRef = useRef(null);
    const videoRefs = useRef([]);
    const hoverCapableRef = useRef(false);
    const [activeIndex, setActiveIndex] = useState(0);
    // Mirrors `activeIndex` synchronously for the drag effect below, which is
    // set up once (stable deps) and would otherwise close over a stale index —
    // state updates aren't visible until the next render.
    const activeIndexRef = useRef(0);
    // A clone of the last/first item is rendered on either end of the track
    // (see `extendedItems` below) so the loop can animate *through* an edge
    // instead of jumping back to it. `displayPosRef` is the position actually
    // painted onto the track right now, which — only while a wrap transition
    // is in flight — differs from `activeIndex + cloneOffset`.
    const displayPosRef = useRef(1);
    // Finalizes an in-flight wrap: snaps the track off the clone and onto the
    // real slide at the same visual position, with no animation. Set while a
    // wrap transition is playing; cleared once it fires. Invoked early if
    // another wrap or gesture starts before the transition naturally ends, so
    // the track never gets left settled on a clone.
    const pendingSnapRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    // Only worth cloning once there's more than one real slide to loop through.
    const cloneOffset = items.length > 1 ? 1 : 0;
    const extendedItems = useMemo(() => (items.length > 1 ? [items[items.length - 1], ...items, items[0]] : items), [items]);
    useEffect(() => {
        hoverCapableRef.current =
            typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
    }, []);
    // Paint the track's position directly (not via a JSX inline style) so a
    // live drag — which also writes `trackRef.current.style` on every pointer
    // move — and a settled render never disagree about who owns the DOM.
    const paint = useCallback((index, offsetPx, animate) => {
        const el = trackRef.current;
        if (!el)
            return;
        el.style.transition = animate ? 'transform 0.3s ease-in-out' : 'none';
        el.style.transform = `translateX(calc(${index * -100}% + ${offsetPx}px))`;
    }, []);
    // Jumps straight to a real slide (dot tap, or a drag release that didn't
    // cross an end) and animates the track there directly — no clone involved.
    const goToIndex = useCallback((idx) => {
        var _a;
        (_a = pendingSnapRef.current) === null || _a === void 0 ? void 0 : _a.call(pendingSnapRef);
        const clamped = Math.max(0, Math.min(items.length - 1, idx));
        activeIndexRef.current = clamped;
        setActiveIndex(clamped);
        const displayPos = clamped + cloneOffset;
        displayPosRef.current = displayPos;
        paint(displayPos, 0, true);
    }, [items.length, cloneOffset, paint]);
    // Drag release crossed an end: animate onto the clone at the far side
    // (continuing in the same direction the drag was already moving), then —
    // once that transition finishes — snap invisibly onto the real slide at
    // the same visual position.
    const wrapAndSettle = useCallback((targetRealIndex, clonePos) => {
        var _a;
        (_a = pendingSnapRef.current) === null || _a === void 0 ? void 0 : _a.call(pendingSnapRef);
        activeIndexRef.current = targetRealIndex;
        setActiveIndex(targetRealIndex);
        displayPosRef.current = clonePos;
        paint(clonePos, 0, true);
        const el = trackRef.current;
        if (!el)
            return;
        const settlePos = targetRealIndex + 1;
        const finalize = () => {
            pendingSnapRef.current = null;
            el.removeEventListener('transitionend', onTransitionEnd);
            paint(settlePos, 0, false);
            displayPosRef.current = settlePos;
        };
        const onTransitionEnd = (event) => {
            if (event.propertyName === 'transform')
                finalize();
        };
        pendingSnapRef.current = finalize;
        el.addEventListener('transitionend', onTransitionEnd);
    }, [paint]);
    // Reset to the first slide whenever the media set changes (e.g. a swatch
    // swaps the colourway). `items` is memoised by the parent, so this only
    // fires on a genuine change, not on every render.
    useEffect(() => {
        var _a;
        (_a = pendingSnapRef.current) === null || _a === void 0 ? void 0 : _a.call(pendingSnapRef);
        activeIndexRef.current = 0;
        setActiveIndex(0);
        displayPosRef.current = cloneOffset;
        paint(cloneOffset, 0, false);
    }, [items, cloneOffset, paint]);
    // Drive video playback off the active slide + hover state. Indexed against
    // `extendedItems`, since that's what's actually rendered — the transient
    // clone slides are never the active one, so they never autoplay.
    useEffect(() => {
        var _a;
        const activePos = activeIndex + cloneOffset;
        videoRefs.current.forEach((video, i) => {
            var _a;
            if (!video)
                return;
            const shouldPlay = i === activePos && ((_a = extendedItems[i]) === null || _a === void 0 ? void 0 : _a.isVideo) && (hoverCapableRef.current ? isHovered : true);
            if (shouldPlay) {
                setIsVideoLoading(true);
                video.play().catch(() => setIsVideoLoading(false));
            }
            else {
                video.pause();
                if (i !== activePos)
                    video.currentTime = 0;
            }
        });
        if (!((_a = extendedItems[activePos]) === null || _a === void 0 ? void 0 : _a.isVideo))
            setIsVideoLoading(false);
    }, [activeIndex, cloneOffset, isHovered, extendedItems]);
    // Drag-to-page — shared by mouse and (single-finger) touch. Tracks the raw
    // pointer delta and paints it straight onto the track every move, so paging
    // never depends on the browser's own scroll/gesture recognition. Only
    // commits to a horizontal page once the gesture is clearly more horizontal
    // than vertical, so a vertical swipe still scrolls the page normally.
    useEffect(() => {
        const el = trackRef.current;
        if (!el)
            return;
        const AXIS_THRESHOLD = 5;
        const drag = {
            active: false,
            axisLocked: null,
            startX: 0,
            startY: 0,
            lastX: 0,
        };
        const pointFromMouse = (event) => ({ x: event.clientX, y: event.clientY });
        const pointFromTouch = (event) => {
            const t = event.touches[0];
            return t ? { x: t.clientX, y: t.clientY } : null;
        };
        const start = (x, y) => {
            var _a;
            // If a previous wrap's transition is still in flight, finalize it now
            // (an instantaneous, invisible jump from clone to real slide) so this
            // new drag starts from a stable baseline.
            (_a = pendingSnapRef.current) === null || _a === void 0 ? void 0 : _a.call(pendingSnapRef);
            drag.active = true;
            drag.axisLocked = null;
            drag.startX = x;
            drag.startY = y;
            drag.lastX = x;
        };
        // Returns true if the caller should suppress its own default handling
        // (native scroll / text selection) — i.e. once we've committed to paging.
        const move = (x, y) => {
            if (!drag.active)
                return false;
            drag.lastX = x;
            const dx = x - drag.startX;
            const dy = y - drag.startY;
            if (!drag.axisLocked) {
                if (Math.abs(dx) < AXIS_THRESHOLD && Math.abs(dy) < AXIS_THRESHOLD)
                    return false;
                // Whichever axis moved further decides this gesture, same as a native
                // scroller committing to one direction once intent is clear.
                drag.axisLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
                if (drag.axisLocked === 'x')
                    el.setAttribute('data-dragging', 'true');
            }
            if (drag.axisLocked !== 'x')
                return false;
            paint(displayPosRef.current, dx, false);
            return true;
        };
        const end = () => {
            if (!drag.active)
                return false;
            drag.active = false;
            const wasPaging = drag.axisLocked === 'x';
            drag.axisLocked = null;
            if (!wasPaging)
                return false; // pure tap/click — let the anchor navigate
            el.removeAttribute('data-dragging');
            const dx = drag.lastX - drag.startX;
            const current = activeIndexRef.current;
            const last = items.length - 1;
            // Any committed horizontal drag pages fully, however far it went —
            // dragging a quarter of the way (or the full width) both complete the
            // swipe, exactly like paging through an Instagram post. `dx === 0` is
            // the one case with no net direction (dragged out past the axis-lock
            // threshold and straight back to the start) — treated as a revert.
            if (dx < 0 && current === last) {
                // Paged forward past the last slide: continue onto the clone of the
                // first slide, then snap onto the real one — looks like the track
                // just keeps going, never like a reset.
                wrapAndSettle(0, items.length + 1);
            }
            else if (dx > 0 && current === 0) {
                wrapAndSettle(last, 0);
            }
            else {
                const target = dx === 0 ? current : dx < 0 ? current + 1 : current - 1;
                goToIndex(target);
            }
            return true;
        };
        // Touch: the element itself receives the whole gesture natively.
        const onTouchStart = (event) => {
            if (event.touches.length !== 1)
                return;
            const p = pointFromTouch(event);
            if (p)
                start(p.x, p.y);
        };
        const onTouchMove = (event) => {
            if (event.touches.length !== 1)
                return;
            const p = pointFromTouch(event);
            // Once paging, stop the page from also trying to scroll under the finger.
            if (p && move(p.x, p.y))
                event.preventDefault();
        };
        const onTouchEnd = () => {
            end();
        };
        // Mouse: track only fires mousedown; the drag can leave the element (or
        // even the window), so mousemove/mouseup are bound on the document for
        // the duration of the press, then torn down.
        const onMouseMove = (event) => {
            const p = pointFromMouse(event);
            if (move(p.x, p.y))
                event.preventDefault();
        };
        const onMouseUp = (event) => {
            const wasPaging = end();
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.userSelect = '';
            if (!wasPaging)
                return;
            // Swallow the click that ends the drag so releasing over the card
            // doesn't navigate the wrapping `<a>`.
            const swallowClick = (ce) => {
                ce.preventDefault();
                ce.stopPropagation();
            };
            document.addEventListener('click', swallowClick, { capture: true, once: true });
            setTimeout(() => document.removeEventListener('click', swallowClick, { capture: true }), 100);
        };
        const onMouseDown = (event) => {
            if (event.button !== 0)
                return;
            start(event.clientX, event.clientY);
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', onMouseMove, { passive: false });
            document.addEventListener('mouseup', onMouseUp);
        };
        // Block the browser's native image/anchor drag so the pointer stays with us.
        const onDragStart = (event) => event.preventDefault();
        el.addEventListener('touchstart', onTouchStart, { passive: true });
        // Must be non-passive: once a drag commits to the horizontal axis we call
        // `preventDefault` so the page doesn't also try to scroll under the finger.
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd, { passive: true });
        el.addEventListener('touchcancel', onTouchEnd, { passive: true });
        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('dragstart', onDragStart);
        return () => {
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('touchend', onTouchEnd);
            el.removeEventListener('touchcancel', onTouchEnd);
            el.removeEventListener('mousedown', onMouseDown);
            el.removeEventListener('dragstart', onDragStart);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.userSelect = '';
        };
    }, [goToIndex, wrapAndSettle, items.length, paint]);
    if (items.length === 0)
        return null;
    const renderMedia = (item, i) => item.isVideo ? (h("video", { ref: el => (videoRefs.current[i] = el), src: item.url, poster: item.poster, muted: true, playsInline: true, loop: true, preload: "none", "aria-hidden": "true", class: styles.slideMedia, onPlaying: () => setIsVideoLoading(false), onWaiting: () => setIsVideoLoading(true), onStalled: () => setIsVideoLoading(true) })) : (h("img", { src: item.url, srcSet: item.srcSet, sizes: item.sizes, alt: item.alt, class: styles.slideMedia, loading: i === cloneOffset && imagePriority ? 'eager' : 'lazy', fetchPriority: i === cloneOffset && imagePriority ? 'high' : 'auto' }));
    // Single media item: render it plainly, no track/controls.
    if (items.length === 1) {
        return h("div", { class: styles.carousel }, renderMedia(items[0], 0));
    }
    const stop = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };
    return (h("div", { class: styles.carousel, onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false) },
        h("div", { class: styles.track, ref: trackRef }, extendedItems.map((item, i) => (h("div", { class: styles.slide, key: i }, renderMedia(item, i))))),
        isVideoLoading && ((_a = items[activeIndex]) === null || _a === void 0 ? void 0 : _a.isVideo) && (h("div", { class: styles.loader, "aria-hidden": "true" })),
        h("div", { class: styles.dots }, items.map((item, i) => (h("button", { key: item.url, type: "button", class: `${styles.dot} ${i <= activeIndex ? styles.dotFilled : ''} ${i === activeIndex ? styles.dotActive : ''}`, "aria-label": `Go to image ${i + 1}`, "aria-current": i === activeIndex ? 'true' : undefined, onClick: event => {
                stop(event);
                goToIndex(i);
            } }))))));
}
